import type { FeatureKey, Features } from "./features";

/**
 * The identity providers a shopper can sign in with besides the emailed code, each behind its own flag.
 * All of them go through Entra External ID, which federates with them (`npm run setup:entra` adds each);
 * the sign-in page has a button per provider whose flag is on, and `domainHint` sends the shopper straight
 * to that provider instead of Entra's own page: `Google` (capitalized: Entra refuses `google` with
 * AADSTS90023), `facebook` and `apple` for Entra's built-in providers, and the issuer's host for a
 * custom OpenID Connect one (Microsoft accounts, LinkedIn). A hint Entra doesn't know shows its own page.
 * `label` is the button's wording, as each provider's guidelines allow: Microsoft's only "Sign in with
 * Microsoft" (or "Sign in"); the rest "Continue with …".
 */
// prettier-ignore
export const SIGN_IN_PROVIDERS = [
  { id: "microsoft", name: "Microsoft", label: "Sign in with Microsoft", feature: "signInMicrosoft", domainHint: "login.live.com" },
  { id: "google", name: "Google", label: "Continue with Google", feature: "signInGoogle", domainHint: "Google" },
  { id: "apple", name: "Apple", label: "Continue with Apple", feature: "signInApple", domainHint: "apple" },
  { id: "facebook", name: "Facebook", label: "Continue with Facebook", feature: "signInFacebook", domainHint: "facebook" },
  { id: "linkedin", name: "LinkedIn", label: "Continue with LinkedIn", feature: "signInLinkedIn", domainHint: "www.linkedin.com" },
] as const satisfies readonly { id: string; name: string; label: string; feature: FeatureKey; domainHint: string }[];

export type SignInProvider = (typeof SIGN_IN_PROVIDERS)[number];
export type SignInProviderId = SignInProvider["id"];

/** The providers whose flags are on, in the order the sign-in page lists them. */
export const signInProviders = (features: Features) => SIGN_IN_PROVIDERS.filter((p) => features[p.feature]);

/** The provider `id` names, if its flag is on. */
export const signInProvider = (features: Features, id: string | null | undefined) =>
  signInProviders(features).find((p) => p.id === id);
